import { useRef } from 'react';
import { FieldError } from 'shared/components/Input';
import { extractDigits } from 'shared/utilities/digits';

const LENGTH = 6;

export function OtpField({
  id = 'otp',
  label,
  value = '',
  onChange,
  error,
  disabled = false,
}) {
  const inputsRef = useRef([]);
  const digits = extractDigits(value, LENGTH).split('');

  function emit(next) {
    onChange(extractDigits(next, LENGTH));
  }

  function handleChange(index, raw) {
    if (raw.length > 1) {
      emit(raw);
      const filled = extractDigits(raw, LENGTH);
      const focusAt = Math.min(filled.length, LENGTH - 1);
      inputsRef.current[focusAt]?.focus();
      return;
    }
    const next = Array.from({ length: LENGTH }, (_, i) => digits[i] || '');
    next[index] = extractDigits(raw).slice(-1);
    emit(next.join(''));
    if (extractDigits(raw) && index < LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index, event) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handlePaste(event) {
    const text = event.clipboardData?.getData('text') || '';
    if (!text) {
      return;
    }
    event.preventDefault();
    emit(text);
    const filled = extractDigits(text, LENGTH);
    inputsRef.current[Math.min(filled.length, LENGTH - 1)]?.focus();
  }

  return (
    <fieldset className="otp-field">
      <legend className="field-label">{label}</legend>
      <div className="otp-grid" role="group" aria-label={label} onPaste={handlePaste} dir="ltr">
        {Array.from({ length: LENGTH }, (_, index) => (
          <input
            key={index}
            id={index === 0 ? id : undefined}
            ref={(node) => { inputsRef.current[index] = node; }}
            className={['otp-input', error ? 'input-error' : ''].filter(Boolean).join(' ')}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={LENGTH}
            value={digits[index] || ''}
            disabled={disabled}
            dir="ltr"
            aria-label={`${label} ${index + 1}`}
            aria-invalid={error ? true : undefined}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
          />
        ))}
      </div>
      <FieldError>{error}</FieldError>
    </fieldset>
  );
}
